import { MutableRefObject } from 'react';
import { CoreBox, CoreTypography } from '@youscience/core';
import { carnegieMap, locations, religiousAffiliation, School } from '@common-types';

import { getSchoolInstitutionLabel } from '@utils/description';

import CheckMarkIcon from '@images/checkmark-in-cercle.svg?react';
import UrlIcon from '@images/url.svg?react';
import AssociateIcon from '@images/AwardsDegreesIcons/associate.svg?react';
import CertificateIcon from '@images/AwardsDegreesIcons/certificate.svg?react';
import BachelorIcon from '@images/AwardsDegreesIcons/bachelor.svg?react';
import GraduateIcon from '@images/AwardsDegreesIcons/graduate.svg?react';

import {
  sxStyles,
  StyledGeneralInformationWrap,
  StyledGeneralInformationInner,
  StyledSchoolDetails,
  StyledDegree,
} from './GeneralInformation.styles';
import { PageSectionIds } from '@constants/schoolDetailsPage';
import { SectionLinkOffset } from '@pages/SchoolDetails/SchoolDetails.styles';

const AWARDS_DEGREES = [
  {
    id: 1,
    icon: <AssociateIcon />,
    name: 'Associate',
    degreeValue: 2,
  },
  {
    id: 2,
    icon: <CertificateIcon />,
    name: 'Certificate',
    degreeValue: 1,
  },
  {
    id: 3,
    icon: <BachelorIcon />,
    name: 'Bachelor',
    degreeValue: 3,
  },
  {
    id: 4,
    icon: <GraduateIcon />,
    name: 'Graduate',
    degreeValue: 4,
  },
];

interface GeneralInformationProps {
  scrollToRef: MutableRefObject<HTMLAnchorElement | null>;
  school: School;
}

export const GeneralInformation = (props: GeneralInformationProps) => {
  const {
    scrollToRef,
    school: { directory, characteristics, collegeScorecard },
  } = props;

  const schoolAwardDegree = collegeScorecard?.latest?.school?.degrees_awarded?.predominant;
  const schoolInstitutionType = directory?.demographics?.institutionType;
  const schoolUrl = directory?.links.url;
  const schoolWebsiteLink = schoolUrl?.match(/^[a-zA-Z]+:\/\//) ? schoolUrl : `https://${schoolUrl}`;
  const schoolType = carnegieMap.get(directory?.demographics?.carnegieSizeSetting);
  const schoolTypeLabel = schoolType?.includes('year') ? `${schoolType}s` : schoolType;

  const isHasOtherHighlight =
    directory?.demographics.isTribal ||
    directory?.demographics.isHistoricallyBlack ||
    collegeScorecard?.latest?.school?.women_only ||
    collegeScorecard?.latest?.school?.men_only;

  const getRegionLabel = () => {
    const location = locations.find((loc) => loc.states.find((state) => state.value === directory?.address.state));

    return location?.label;
  };

  const getClassesOfferedLabel = () => {
    // TODO check type isDistanceLearning and hasNoDistanceEducation
    if ((characteristics?.isDistanceLearning as unknown as number) === 1) {
      return 'Online only';
    }
    if ((characteristics as unknown as { hasNoDistanceEducation: number })?.hasNoDistanceEducation === 1) {
      return 'In-person only';
    }
    if (
      [2, -1, -2].includes(characteristics?.isDistanceLearning as unknown as number) ||
      [0, -1, -2].includes((characteristics as unknown as { hasNoDistanceEducation: number })?.hasNoDistanceEducation)
    ) {
      return 'In-person and online';
    }

    return '';
  };

  return (
    <CoreBox sx={sxStyles.container}>
      <SectionLinkOffset id={PageSectionIds['general-information']} ref={scrollToRef} />

      <CoreTypography sx={sxStyles.sectionTitle} variant="h4" component="h3">
        General information
      </CoreTypography>

      <StyledGeneralInformationWrap>
        <CoreTypography sx={sxStyles.sectionSubtitle} variant="h5" component="h4">
          School details
        </CoreTypography>

        <StyledGeneralInformationInner>
          <StyledSchoolDetails>
            <CoreBox>
              <CoreBox>
                <CoreTypography sx={sxStyles.schoolSubtitle} component="div" variant="body2">
                  Name
                </CoreTypography>

                <CoreTypography sx={sxStyles.schoolName} component="h3" variant="h3">
                  {directory?.name}
                </CoreTypography>
              </CoreBox>

              <CoreBox sx={sxStyles.schoolLocation}>
                <CoreBox>
                  <CoreTypography sx={sxStyles.schoolSubtitle} component="div" variant="body2">
                    Location
                  </CoreTypography>

                  <CoreTypography component="div" variant="h6">
                    {directory?.address.city}
                  </CoreTypography>
                </CoreBox>
                <CoreBox>
                  <CoreTypography sx={sxStyles.schoolSubtitle} component="div" variant="body2">
                    Region
                  </CoreTypography>

                  <CoreTypography component="div" variant="h6">
                    {getRegionLabel()}
                  </CoreTypography>
                </CoreBox>
              </CoreBox>

              <CoreBox sx={sxStyles.schoolType}>
                <CoreBox>
                  <CoreTypography sx={sxStyles.schoolSubtitle} component="div" variant="body2">
                    Control
                  </CoreTypography>

                  <CoreTypography component="div" variant="h6">
                    {getSchoolInstitutionLabel(schoolInstitutionType)}
                  </CoreTypography>
                </CoreBox>
                <CoreBox>
                  <CoreTypography sx={sxStyles.schoolSubtitle} component="div" variant="body2">
                    School type
                  </CoreTypography>

                  <CoreTypography component="div" variant="h6">
                    {schoolTypeLabel}
                  </CoreTypography>
                </CoreBox>
              </CoreBox>

              <CoreBox sx={sxStyles.schoolWebsite}>
                <CoreTypography variant="overline" component="h5">
                  College website
                </CoreTypography>

                <CoreBox sx={sxStyles.schoolUrl}>
                  <UrlIcon />

                  <a rel="noreferrer" target="_blank" href={schoolWebsiteLink}>
                    {directory?.links.url}
                  </a>
                </CoreBox>
              </CoreBox>
            </CoreBox>

            <CoreBox>
              <CoreTypography sx={sxStyles.additionalTitle} variant="overline" component="h5">
                Additional:
              </CoreTypography>

              <CoreBox sx={sxStyles.classesOffered}>
                <CoreTypography sx={sxStyles.schoolSubtitle} component="div" variant="body2">
                  Classes offered
                </CoreTypography>

                <CoreTypography component="div" variant="h6">
                  {getClassesOfferedLabel()}
                </CoreTypography>
              </CoreBox>

              <CoreBox>
                <CoreTypography sx={sxStyles.schoolSubtitle} component="div" variant="body2">
                  Religious Affiliation
                </CoreTypography>
                {characteristics?.religiousAffiliation ? (
                  <CoreTypography component="div" variant="h6">
                    {religiousAffiliation.get(characteristics?.religiousAffiliation)}
                  </CoreTypography>
                ) : (
                  <CoreTypography component="div" variant="h6">
                    Not affiliated with a religion
                  </CoreTypography>
                )}
              </CoreBox>

              {isHasOtherHighlight && (
                <CoreBox sx={sxStyles.otherHighlights}>
                  <CoreTypography variant="overline" component="h5">
                    Other Highlights:
                  </CoreTypography>

                  {directory?.demographics.isTribal && (
                    <CoreBox sx={sxStyles.schoolHighlights}>
                      <CoreBox>
                        <CheckMarkIcon />
                      </CoreBox>

                      <CoreTypography>Tribal</CoreTypography>
                    </CoreBox>
                  )}

                  {directory?.demographics.isHistoricallyBlack && (
                    <CoreBox sx={sxStyles.schoolHighlights}>
                      <CoreBox>
                        <CheckMarkIcon />
                      </CoreBox>

                      <CoreTypography>Historically black school and university</CoreTypography>
                    </CoreBox>
                  )}

                  {collegeScorecard?.latest?.school?.men_only && (
                    <CoreBox sx={sxStyles.schoolHighlights}>
                      <CoreBox>
                        <CheckMarkIcon />
                      </CoreBox>

                      <CoreTypography>Men-only school</CoreTypography>
                    </CoreBox>
                  )}

                  {collegeScorecard?.latest?.school?.women_only && (
                    <CoreBox sx={sxStyles.schoolHighlights}>
                      <CoreBox>
                        <CheckMarkIcon />
                      </CoreBox>

                      <CoreTypography>Woman-only school</CoreTypography>
                    </CoreBox>
                  )}
                </CoreBox>
              )}
            </CoreBox>
          </StyledSchoolDetails>

          {schoolAwardDegree && (
            <CoreBox sx={sxStyles.awardsDegreesWrap}>
              <CoreTypography variant="overline" component="h5">
                Most students that attend this school earn a:
              </CoreTypography>

              <CoreBox sx={sxStyles.awardsDegrees}>
                {AWARDS_DEGREES.map((degree) => {
                  const isAwardDegree = degree.degreeValue === schoolAwardDegree;

                  return (
                    <StyledDegree key={degree.id} isAwardDegree={isAwardDegree}>
                      <CoreBox>{degree.icon}</CoreBox>

                      <div>{degree.name}</div>
                    </StyledDegree>
                  );
                })}
              </CoreBox>
            </CoreBox>
          )}
        </StyledGeneralInformationInner>
      </StyledGeneralInformationWrap>
    </CoreBox>
  );
};
